<template>
  <v-container fluid class="page--case-battles pos-relative d-flex">
    <v-container class="game-container">
      <v-row class="pt-8">
        <v-col cols="12" class="d-flex flex-wrap align-center mb-4">
          <h3 class="text-body-1 font-weight-bold mr-3 primary700 primary200--text px-4 py-3 lh-1 text-uppercase">
            {{ games.length }} games
          </h3>

          <div class="py-3 px-4 bg-ui-panel text-uppercase grey100--text text-body-2 lh-1 mr-3">
            <span class="font-weight-semibold">{{ joinable.length }} joinable</span>
          </div>

          <div class="py-3 px-4 blue700 blue100--text lh-1 text-body-2">
            <scrap class="mr-1 font-weight-bold" :amount="totalValue" />
            <span class="text-uppercase font-weight-bold ">Total</span>
          </div>

          <v-spacer />

          <v-btn
            color="green700 green200--text" tile
            class="px-5 font-weight-bold" large
            depressed :ripple="false" height="38"
            :to="{name: 'Crate Battle Create'}"
          >
            Create battle

            <v-tooltip v-if="config.xpCreatorMultiplier > 1" top color="grey400" content-class="rounded-0">
              <template #activator="{ on }">
                <div
                  class="xp-bonus font-weight-semibold lh-1 text-caption green800 green300--text"
                  v-on="on"
                >
                  +{{ (config.xpCreatorMultiplier - 1) * 100 | toLocale }}% XP
                </div>
              </template>
              <span>Creator XP bonus</span>
            </v-tooltip>
          </v-btn>
        </v-col>

        <v-col cols="12">
          <transition-group name="t-listings" tag="div">
            <BattleListing
              v-for="game in games" :key="game.id"
              :game="game" :user="user"
              class="mb-5"
              :class="{'is-joinable': game.joinable}"
              @join="onJoin(game, $event)"
              @inspect="onInspectCrate"
            />
          </transition-group>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CaseBattlesGame from '@/store/models/games/case-battles/CaseBattlesGame'
import OnboardingMixin from '@/mixins/onboarding'
import BattleListing from './components/BattleListing'
import { GAME_STATUS } from './utils'

export default {
  metaInfo: {
    title: 'Crate Battles',
  },
  components: {
    BattleListing,
  },
  mixins: [OnboardingMixin],
  data: () => ({
    loading: {},
  }),
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
    }),
    config() {
      return this.conga.games.caseBattles
    },
    games() {
      const { user } = this
      return CaseBattlesGame.query()
        .where(game => {
          if (game.private) {
            if (user?.steamid === game.creatorId) return true
            return false
          }
          return true
        })
        .orderBy(game => {
          if (game.status === GAME_STATUS.complete && !game.canShowWinners) {
            return GAME_STATUS.rolling
          }
          if (game.status === GAME_STATUS.locked) {
            return GAME_STATUS.rolling
          }

          return game.status
        }, 'asc')
        .orderBy('price', 'desc')
        .get()
    },
    joinable() {
      return this.games.filter(i => i.joinable)
    },
    totalValue() {
      return this.games.reduce((acc, v) => acc + v.price, 0)
    },
  },
  methods: {
    ...mapActions({
      joinBattle: 'games/case-battles/join',
    }),
    onInspectCrate(crate) {
      this.$modal.open('battle-inspect-crate', { maxWidth: 650 }, { crate })
    },
    async onJoin(game, teamId) {
      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      if (!teamId) {
        this.$modal.open('battle-join', { maxWidth: 350 }, { game, teamId })
        return
      }

      const hasWarning = game.isUneven || game.isCursed
      if (hasWarning && !this.onboardingHasSeen('battle-join-warning')) {
        this.$modal.open('battle-join', { maxWidth: 350 }, { game, teamId })
        return
      }

      try {
        await this.joinBattle({
          id: game.id,
          teamId,
        })

        this.$router.push({
          name: 'Crate Battle',
          params: { id: game.id },
        }).catch(() => {})

        this.$toast.success('Battle joined')
      } catch (error) {
        this.$toast.error(error.message)
      }
    },
  },
}

</script>

<style lang="scss" scoped>
.page--case-battles {
  min-height: calc(100vh - var(--top));
  background: grey(900); // fallback
  // TODO
  background: url('../../../assets/img/games/coinflip/bg.jpg') no-repeat top center;
  background-size: cover;

  &::before {
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(rgba(grey(800), 0.95) 50%, rgba(grey(900), 0.98) 80%);
    content: ' ';
    // pointer-events: none;
  }
}

.game-container {
  z-index: 1;
  max-width: 1200px;
}

.battle-listing.is-joinable + :not(.is-joinable) {
  margin-top: 48px !important;
}

.battle-listing {
  transition: transform 0.6s ease;
}

.xp-bonus {
  position: absolute;
  z-index: 3;
  top: 0;
  right: 0;
  padding: 4px 8px;
  transform: translate(50%, -120%) rotate(3deg);
}

.t-listings {
  &-enter,
  &-leave-to {
    opacity: 0;
    transform: scale(0.8);
  }

  &-enter-to {
    opacity: 1;
    transform: scale(1);
  }

  &-enter-active {
    ::v-deep .battle-player .v-btn {
      display: none;
    }
  }

  &-move {
    ::v-deep .battle-player .v-btn {
      display: none;
    }
  }
}

</style>
